// Disposal: where Athens trucks dumped, what the loads weighed, whether the
// tickets agree with Scale-Pak, what the sites billed, and which roll-off boxes
// sit idle. Read from the Soft-Pak replica through /disposal
// (backend/src/routes/disposal.js). Replaces 12 disposal Crystal reports; the
// rules behind yard, commodity, division and G/L are in GitLab
// db2-replica/softpak-reporting docs/disposal.md.
//
// The daily totals arrive once per date range; the yard, line and site filters
// are applied here, so changing one needs no new request. The lists (tickets,
// work order lines, charges, containers) are fetched per tab.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  Truck, Scale, DollarSign, AlertTriangle, ClipboardX, Weight, Download, Link2, Link2Off, Receipt, Box,
} from 'lucide-react';
import { api } from '../services/api';
import PageHeader from '../components/Shared/PageHeader';
import StatTile from '../components/Inventory/StatTile';
import { SelectOne, TextField } from '../components/Expenses/FilterBar';
import { fmtDate, fmtMoney, fmtNumber } from '../components/Inventory/format';
import { rangeFor, pacificToday } from './ScalePak';
import { btn, c, card, chart, t, tbl } from '../theme';

const RANGES = [
  { value: '1', label: 'Today' },
  { value: 'Y', label: 'Yesterday' },
  { value: '7', label: 'Last 7 days' },
  { value: 'MTD', label: 'Month to date' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'C', label: 'Custom…' },
];
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'overweight', label: 'Overweight' },
  { id: 'tickets', label: 'Tickets & posting' },
  { id: 'cost', label: 'Disposal cost' },
  { id: 'idle', label: 'Idle containers' },
];
// Lines in fixed stacking order; categorical is adjacent-pairs validated, which is what a stack needs.
// Transfer: trailers hauling out of our own MRFs (Soft-Pak company 58). Their tons were already
// counted when the collection trucks dumped at the MRF, so the collection tiles leave them out
// and the chart shows them as their own series.
const LINES = ['Roll-off', 'Commercial', 'Residential', 'Transfer', 'Other'];
const LINE_COLOR = { 'Roll-off': chart.categorical[0], Commercial: chart.categorical[1], Residential: chart.categorical[2], Transfer: chart.categorical[3], Other: chart.other };
const LIST_DAYS = 93;

const n = (v) => Number(v || 0);
const money2 = (v) => (v === null || v === undefined || Number.isNaN(v) ? '—' : `$${Number(v).toFixed(2)}`);
const tons2 = (v) => (v === null || v === undefined ? '—' : n(v).toFixed(2));
const dayLabel = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'UTC' });
const day = (d) => String(d ?? '').slice(0, 10);

function addDays(d, k) {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + k);
  return x.toISOString().slice(0, 10);
}

// Daily rows after the in-page filters.
export function applyFilters(rows, f) {
  return rows.filter((r) => (!f.yard || r.yard === f.yard)
    && (!f.line || r.line_of_business === f.line)
    && (!f.site || r.site_code === f.site));
}

export function totals(rows) {
  const o = { tons: 0, loads: 0, transferTons: 0, transferLoads: 0, overweight: 0, negative: 0, ratedTons: 0, ratedCost: 0, ratedContract: 0,
    thirdPartyTons: 0, thirdPartyCost: 0, athensCost: 0 };
  for (const r of rows) {
    // Tons and loads count collection only: a transfer load is tons already counted at the MRF.
    if (r.line_of_business === 'Transfer') { o.transferTons += n(r.tons); o.transferLoads += n(r.loads); }
    else { o.tons += n(r.tons); o.loads += n(r.loads); }
    o.overweight += n(r.overweight_loads); o.negative += n(r.negative_tickets);
    o.ratedTons += n(r.rated_tons); o.ratedCost += n(r.rated_cost); o.ratedContract += n(r.rated_contract_cost);
    // Cost paid out is third-party sites, transfer included; Athens facilities' charges are internal.
    if (r.is_internal_site) o.athensCost += n(r.cost);
    else { o.thirdPartyTons += n(r.tons); o.thirdPartyCost += n(r.cost); }
  }
  return o;
}

// One row per site: what was paid against what the contract rate would have cost.
export function bySite(rows) {
  const m = new Map();
  for (const r of rows) {
    const k = r.site_code || '—';
    const x = m.get(k) || { key: k, name: r.site_name || k, internal: !!r.is_internal_site, loads: 0, tons: 0, cost: 0, ratedTons: 0, ratedCost: 0, ratedContract: 0 };
    x.loads += n(r.loads); x.tons += n(r.tons); x.cost += n(r.cost);
    x.ratedTons += n(r.rated_tons); x.ratedCost += n(r.rated_cost); x.ratedContract += n(r.rated_contract_cost);
    m.set(k, x);
  }
  return [...m.values()].sort((a, b) => b.tons - a.tons);
}

function sumBy(rows, key, label, value = 'tons') {
  const m = new Map();
  for (const r of rows) {
    const k = r[key] ?? '—';
    const x = m.get(k) || { key: k, label: label(r), tons: 0, loads: 0 };
    x.tons += n(r[value]);
    x.loads += n(r.loads);
    m.set(k, x);
  }
  return [...m.values()].filter((x) => x.tons > 0).sort((a, b) => b.tons - a.tons);
}

function byDay(rows, from, to) {
  const days = new Map();
  for (let d = from; d <= to; d = addDays(d, 1)) days.set(d, { day: d, ...Object.fromEntries(LINES.map((l) => [l, 0])) });
  for (const r of rows) {
    const d = days.get(day(r.disposal_date));
    if (d) d[LINES.includes(r.line_of_business) ? r.line_of_business : 'Other'] += n(r.tons);
  }
  return [...days.values()].map((d) => ({ ...d, ...Object.fromEntries(LINES.map((l) => [l, Math.round(d[l])])) }));
}

// Unposted tickets by age, in days since the ticket date.
export const AGE_BANDS = [[0, 2, '0–2 days'], [3, 7, '3–7 days'], [8, 30, '8–30 days'], [31, Infinity, 'Over 30 days']];
export function ageBands(rows, today) {
  const out = AGE_BANDS.map(([, , label]) => ({ key: label, label, tons: 0, loads: 0 }));
  for (const r of rows) {
    const age = Math.round((Date.parse(today) - Date.parse(day(r.disposal_date))) / 86400000);
    const i = AGE_BANDS.findIndex(([lo, hi]) => age >= lo && age <= hi);
    if (i >= 0) { out[i].tons += 1; out[i].loads += 1; }
  }
  return out;
}

// Division × G/L account, from the charge totals.
export function glMatrix(rows, measure) {
  const accounts = [...new Set(rows.map((r) => r.gl_account))].sort();
  const m = new Map();
  for (const r of rows) {
    const k = r.division ?? '—';
    const x = m.get(k) || { division: k, yard: r.division_yard || '', cells: {} };
    const cell = x.cells[r.gl_account] || { tons: 0, cost: 0 };
    cell.tons += n(r.tons); cell.cost += n(r.cost);
    x.cells[r.gl_account] = cell;
    m.set(k, x);
  }
  const value = (cell) => (!cell ? null : measure === 'tons' ? cell.tons : measure === 'rate' ? (cell.tons ? cell.cost / cell.tons : null) : cell.cost);
  const rowsOut = [...m.values()].sort((a, b) => String(a.division).localeCompare(String(b.division)))
    .map((x) => {
      const all = Object.values(x.cells).reduce((s, cl) => ({ tons: s.tons + cl.tons, cost: s.cost + cl.cost }), { tons: 0, cost: 0 });
      return { ...x, values: accounts.map((a) => value(x.cells[a])), total: value(all) };
    });
  return { accounts, rows: rowsOut };
}

async function download(path, params, name) {
  const blob = await api.getBlob(path, { params });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function Disposal() {
  const [tab, setTab] = useState('overview');
  const [preset, setPreset] = useState('30');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [yard, setYard] = useState('');
  const [line, setLine] = useState('');
  const [site, setSite] = useState('');

  const range = useMemo(() => rangeFor(preset, pacificToday(), custom), [preset, custom]);
  const base = { from: range.from, to: range.to };

  const daily = useQuery({
    queryKey: ['disposal', 'daily', base],
    queryFn: () => api.get('/disposal/daily', { params: base }),
    staleTime: 60_000,
    refetchInterval: range.to === pacificToday() ? 120_000 : false,
  });
  const prevBase = { from: range.prevFrom, to: range.prevTo };
  const prev = useQuery({
    queryKey: ['disposal', 'daily', prevBase],
    queryFn: () => api.get('/disposal/daily', { params: prevBase }),
    staleTime: 10 * 60_000,
    enabled: range.days <= 200,
  });

  const rows = useMemo(() => daily.data?.rows || [], [daily.data]);
  const filters = { yard, line, site };
  const shown = useMemo(() => applyFilters(rows, filters), [rows, yard, line, site]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pick lists from what the date range holds.
  const yards = useMemo(() => sumBy(applyFilters(rows, { ...filters, yard: '' }), 'yard', (r) => r.yard), [rows, line, site]); // eslint-disable-line react-hooks/exhaustive-deps
  const sites = useMemo(() => {
    const m = new Map(rows.map((r) => [r.site_code, r.site_name]));
    if (site && !m.has(site)) m.set(site, site);
    return [...m.entries()].filter(([k]) => k).sort().map(([v, l]) => ({ value: v, label: `${v} · ${l || v}` }));
  }, [rows, site]);
  const pick = (list, value) => [{ value: '', label: 'All' }, ...list, ...(value && !list.some((x) => x.value === value) ? [{ value, label: value }] : [])];

  const ctx = { range, yard, line, site, setYard, setSite, rows, shown, prevRows: prev.data?.rows, loading: daily.isLoading };

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Disposal"
        subtitle="Where our loads were dumped, what they weighed, whether the tickets agree with Scale-Pak and what the sites billed, read live from Soft-Pak. Replaces 12 disposal Crystal reports."
        meta={<span style={s.live}><span style={s.dot} />Live from the Soft-Pak replica · past days refresh at 3:15 and 15:15</span>}
      />

      <div style={card.base}>
        <div style={s.filters}>
          <SelectOne label="Dates" value={preset} onChange={setPreset} options={RANGES} width={150} />
          {preset === 'C' && (
            <>
              <TextField label="From" type="date" value={custom.from} onChange={(v) => setCustom((x) => ({ ...x, from: v }))} width={150} />
              <TextField label="To" type="date" value={custom.to} onChange={(v) => setCustom((x) => ({ ...x, to: v }))} width={150} />
            </>
          )}
          <SelectOne label="Yard" value={yard} onChange={setYard} options={pick(yards.map((x) => ({ value: x.key, label: x.label })), yard)} width={170} />
          <SelectOne label="Line" value={line} onChange={setLine} options={pick(LINES.map((l) => ({ value: l, label: l })), line)} width={140} />
          <SelectOne label="Site" value={site} onChange={setSite} options={pick(sites, site)} width={220} />
        </div>

        <div style={s.tabRow} role="tablist">
          {TABS.map((x) => (
            <button key={x.id} type="button" role="tab" aria-selected={tab === x.id}
                    style={{ ...s.tab, ...(tab === x.id ? s.tabOn : null) }} onClick={() => setTab(x.id)}>
              {x.label}
            </button>
          ))}
        </div>

        {daily.isError && <div style={s.error}>{daily.error.message}</div>}

        {tab === 'overview' && <Overview {...ctx} openTab={setTab} />}
        {tab === 'overweight' && <Overweight {...ctx} />}
        {tab === 'tickets' && <TicketsPosting {...ctx} />}
        {tab === 'cost' && <Cost {...ctx} />}
        {tab === 'idle' && <Idle yard={yard} />}
      </div>
    </div>
  );
}

function Overview({ range, yard, line, site, setYard, setSite, rows, shown, prevRows, loading, openTab }) {
  const now = totals(shown);
  const was = prevRows ? totals(applyFilters(prevRows, { yard, line, site })) : null;
  const tooLong = range.days > LIST_DAYS;
  // The unposted count is live: posting changes after the day, so it is not in the stored totals.
  const unpostedParams = { from: range.from, to: range.to, unposted: 'Y', ...(yard ? { yard } : {}), ...(site ? { site } : {}) };
  const unposted = useQuery({
    queryKey: ['disposal', 'tickets', unpostedParams],
    queryFn: () => api.get('/disposal/tickets', { params: unpostedParams }),
    enabled: !tooLong,
    staleTime: 60_000,
  });
  const unpostedRows = (unposted.data?.rows || []).filter((r) => !line || r.line_of_business === line);
  const series = useMemo(() => byDay(shown, range.from, range.to), [shown, range.from, range.to]);
  const yards = useMemo(() => sumBy(applyFilters(rows, { yard: '', line, site }), 'yard', (r) => r.yard), [rows, line, site]);
  const commodities = useMemo(() => sumBy(shown, 'commodity', (r) => r.commodity), [shown]);
  const siteRows = useMemo(() => bySite(shown), [shown]);
  const paid = now.thirdPartyTons ? now.thirdPartyCost / now.thirdPartyTons : null;
  const paidWas = was && was.thirdPartyTons ? was.thirdPartyCost / was.thirdPartyTons : null;
  const overContract = now.ratedCost - now.ratedContract;

  return (
    <div style={s.panel}>
      <div style={s.tiles}>
        <StatTile icon={<Weight size={16} />} label="Tons collected" value={fmtNumber(Math.round(now.tons))}
                  sub={loading ? 'Loading…' : `plus ${fmtNumber(Math.round(now.transferTons))} t hauled out of our MRFs`} current={now.tons} previous={was?.tons} />
        <StatTile icon={<Truck size={16} />} label="Loads" value={fmtNumber(now.loads)} sub={`collection, ${range.days} day${range.days === 1 ? '' : 's'}`} current={now.loads} previous={was?.loads} />
        <StatTile icon={<DollarSign size={16} />} label="Third-party disposal cost" value={fmtMoney(now.thirdPartyCost)}
                  sub={`plus ${fmtMoney(now.athensCost)} charged at Athens facilities`} current={now.thirdPartyCost} previous={was?.thirdPartyCost} goodDirection="down" />
        <StatTile icon={<Scale size={16} />} label="Paid $/ton" value={money2(paid)} sub="third-party sites only" current={paid} previous={paidWas} goodDirection="down" />
        <StatTile icon={<AlertTriangle size={16} />} tone={now.overweight ? 'warning' : 'neutral'} label="Loads 12 t and over" value={fmtNumber(now.overweight)}
                  sub={now.loads ? `${((now.overweight / now.loads) * 100).toFixed(1)}% of loads` : '—'} onClick={() => openTab('overweight')} />
        <StatTile icon={<ClipboardX size={16} />} tone={unpostedRows.length ? 'danger' : 'neutral'} label="Unposted tickets"
                  value={tooLong ? '—' : unposted.isLoading ? '…' : fmtNumber(unpostedRows.length)}
                  sub={tooLong ? `up to ${LIST_DAYS} days` : `third-party, ${fmtNumber(unpostedRows.filter((r) => Date.parse(pacificToday()) - Date.parse(day(r.disposal_date)) > 7 * 86400000).length)} older than 7 days`}
                  onClick={() => openTab('tickets')} />
      </div>

      <div style={s.grid2}>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Tons per day by line of business<Legend items={LINES.map((l) => [l, LINE_COLOR[l]])} /></div>
          <div style={s.chartBody}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke={chart.grid} />
                <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} minTickGap={18} />
                <YAxis tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => fmtNumber(v)} />
                <Tooltip cursor={{ fill: 'rgba(27,42,74,0.05)' }} labelFormatter={(d) => fmtDate(d)}
                         formatter={(v, name) => [`${fmtNumber(v)} t`, name]}
                         contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${chart.tooltipBorder}` }} />
                {LINES.map((l, i) => (
                  <Bar key={l} dataKey={l} stackId="t" fill={LINE_COLOR[l]} radius={i === LINES.length - 1 ? [4, 4, 0, 0] : undefined} isAnimationActive={false} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Tons by yard<span style={s.headNote}>Click a bar to filter</span></div>
          <div style={s.chartBody}><Ranked items={yards} selected={yard} onPick={(k) => setYard(yard === k ? '' : k)} unit=" t" /></div>
        </div>
      </div>

      <div style={{ ...card.sm, minWidth: 0 }}>
        <div style={s.cardHead}>
          By disposal site
          <span style={s.headNote}>
            {overContract > 0.5 ? `${fmtMoney(overContract)} over contract on loads with a rate · ` : ''}Click a row to filter
          </span>
        </div>
        <div style={s.tableWrapFlat}>
          <table style={s.table}>
            <thead>
              <tr>
                {['Site', 'Type'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}
                {['Loads', 'Tons', 'Cost', 'Paid $/t', 'Contract $/t'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}
                <th style={tbl.thSm}>Rate check</th>
              </tr>
            </thead>
            <tbody>
              {siteRows.slice(0, 40).map((x) => {
                const contract = x.ratedTons ? x.ratedContract / x.ratedTons : null;
                const over = x.ratedCost - x.ratedContract;
                return (
                  <tr key={x.key} style={{ ...tbl.tr, cursor: 'pointer', ...(site === x.key ? s.rowOn : null) }} onClick={() => setSite(site === x.key ? '' : x.key)}>
                    <td style={tbl.tdSm}>{x.name} <span style={s.code}>{x.key}</span></td>
                    <td style={tbl.tdSm}>{x.internal ? 'Athens facility' : 'Third party'}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(x.loads)}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(Math.round(x.tons))}</td>
                    <td style={tbl.tdRSm}>{x.cost ? fmtMoney(x.cost) : '—'}</td>
                    <td style={tbl.tdRSm}>{x.tons > 0 && x.cost ? money2(x.cost / x.tons) : '—'}</td>
                    <td style={tbl.tdRSm}>{money2(contract)}</td>
                    <td style={tbl.tdSm}>
                      {x.internal ? <Pill tone="info">Athens site</Pill>
                        : contract === null ? <Pill>no contract rate</Pill>
                        : Math.abs(over) < 0.5 ? <Pill tone="success">on rate</Pill>
                        : over > 0 ? <Pill tone="danger">{fmtMoney(over)} over</Pill>
                        : <Pill tone="success">{fmtMoney(-over)} under</Pill>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!siteRows.length && <div style={s.empty}>{loading ? 'Loading…' : 'No disposal tickets for these filters.'}</div>}
        </div>
      </div>

      <div style={s.grid2b}>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Tons by commodity<span style={s.headNote}>One grouping for every tab</span></div>
          <div style={s.chartBody}><Ranked items={commodities} unit=" t" /></div>
        </div>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>How these numbers are built</div>
          <div style={{ ...s.chartBody, ...s.note, display: 'grid', gap: '8px' }}>
            <p style={s.p}>One row per disposal ticket an Athens truck brought back: route trucks (the third-party and facility tickets keyed in after the route) and roll-off (the roll-off disposal log). Reversed tickets are left out of every total. Cost is what was keyed on the ticket; the contract rate is the site&apos;s first rate line for the material.</p>
            <p style={s.p}>Yard is the ADV route plan&apos;s operating yard, else the route type&apos;s first letter. Our own scale houses are on the Scale-Pak page: a truck dumping at an Athens MRF already has its ticket here.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Overweight({ range, yard, line, site }) {
  const [limit, setLimit] = useState('12');
  const [source, setSource] = useState('');
  const [exporting, setExporting] = useState(false);
  const tooLong = range.days > LIST_DAYS;
  const minTons = /^\d{1,3}$/.test(limit) ? limit : '12';
  const params = { from: range.from, to: range.to, min_tons: minTons, ...(source ? { source } : {}), ...(yard ? { yard } : {}), ...(site ? { site } : {}) };
  const q = useQuery({
    queryKey: ['disposal', 'tickets', params],
    queryFn: () => api.get('/disposal/tickets', { params }),
    enabled: !tooLong,
    staleTime: 60_000,
  });
  const list = useMemo(() => (q.data?.rows || []).filter((r) => !line || r.line_of_business === line), [q.data, line]);
  const heavy = list.filter((r) => n(r.tons) >= Number(minTons));
  const negative = list.filter((r) => n(r.tons) < 0);
  const top = heavy[0];
  const byYard = useMemo(() => sumBy(heavy.map((r) => ({ ...r, one: 1 })), 'yard', (r) => r.yard, 'one'), [heavy]); // eslint-disable-line react-hooks/exhaustive-deps
  const repeat = useMemo(() => {
    const m = new Map();
    for (const r of heavy) {
      if (!r.vehicle) continue;
      const x = m.get(r.vehicle) || { vehicle: r.vehicle, yard: r.yard, loads: 0, tons: 0 };
      x.loads += 1; x.tons += n(r.tons);
      m.set(r.vehicle, x);
    }
    return [...m.values()].filter((x) => x.loads >= 3).sort((a, b) => b.loads - a.loads || b.tons - a.tons);
  }, [heavy]);

  if (tooLong) return <div style={s.panel}><div style={s.empty}>The overweight list covers up to {LIST_DAYS} days. Choose a shorter range.</div></div>;
  return (
    <div style={s.panel}>
      <div style={s.toolbar}>
        <TextField label="At or over (tons)" value={limit} onChange={(v) => setLimit(v.replace(/[^0-9]/g, '').slice(0, 3))} width={130} />
        <Seg label="Source" value={source} onChange={setSource} options={[['', 'Both'], ['ROUTE', 'Route trucks'], ['ROLL-OFF', 'Roll-off']]} />
        <span style={s.note}>Negative tickets are always listed: they are usually keying errors.</span>
        <ExportButton busy={exporting} disabled={!list.length}
                      onClick={async () => { setExporting(true); try { await download('/disposal/tickets/export.xlsx', params, `disposal-overweight-${range.from}-to-${range.to}.xlsx`); } finally { setExporting(false); } }} />
      </div>
      {q.isError && <div style={s.errorIn}>{q.error.message}</div>}
      <div style={s.tiles}>
        <StatTile icon={<AlertTriangle size={16} />} tone={heavy.length ? 'warning' : 'neutral'} label={`Loads ${minTons} t and over`} value={q.isLoading ? '…' : fmtNumber(heavy.length)} sub={`${range.days} day${range.days === 1 ? '' : 's'}`} />
        <StatTile icon={<Truck size={16} />} label="Route trucks" value={fmtNumber(heavy.filter((r) => r.source === 'route').length)} sub="third-party and facility tickets" />
        <StatTile icon={<Box size={16} />} label="Roll-off" value={fmtNumber(heavy.filter((r) => r.source === 'roll-off').length)} sub="roll-off disposal log" />
        <StatTile icon={<Weight size={16} />} label="Heaviest" value={top ? `${tons2(top.tons)} t` : '—'} sub={top ? `${top.route_code || top.vehicle || ''} · ${fmtDate(top.disposal_date)}` : ''} />
        <StatTile icon={<ClipboardX size={16} />} tone={negative.length ? 'danger' : 'neutral'} label="Negative tickets" value={fmtNumber(negative.length)} sub="check the keying" />
      </div>
      <div style={s.grid2}>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Overweight loads by yard</div>
          <div style={s.chartBody}><Ranked items={byYard} unit="" /></div>
        </div>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Repeat trucks<span style={s.headNote}>3 or more in the range</span></div>
          <div style={s.tableWrapFlat}>
            <table style={s.table}>
              <thead><tr>{['Truck', 'Yard'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}{['Loads', 'Avg t'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}</tr></thead>
              <tbody>
                {repeat.slice(0, 12).map((x) => (
                  <tr key={x.vehicle} style={tbl.tr}>
                    <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{x.vehicle}</td>
                    <td style={tbl.tdSm}>{x.yard}</td>
                    <td style={tbl.tdRSm}>{x.loads}</td>
                    <td style={tbl.tdRSm}>{(x.tons / x.loads).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!repeat.length && <div style={s.empty}>No truck was over the limit three times.</div>}
          </div>
        </div>
      </div>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              {['Date', 'Source', 'Yard · line', 'Route', 'Truck', 'Driver', 'Ticket', 'Site', 'Material', 'Customer'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}
              <th style={tbl.thRSm}>Tons</th>
              <th style={tbl.thSm}>Flags</th>
            </tr>
          </thead>
          <tbody>
            {list.slice(0, 500).map((r) => (
              <tr key={`${r.source}-${r.company_code}-${r.date_number}-${r.route_code}-${r.load_number}-${r.ticket}`} style={tbl.tr}>
                <td style={tbl.tdSm}>{fmtDate(r.disposal_date)}</td>
                <td style={tbl.tdSm}>{r.source === 'route' ? 'Route' : 'Roll-off'}</td>
                <td style={tbl.tdSm}>{r.yard} · {r.line_of_business}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.route_code || '—'}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.vehicle || '—'}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.driver || '—'}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.ticket || '—'}</td>
                <td style={tbl.tdSm} title={r.site_code}>{r.site_name || r.site_code}</td>
                <td style={tbl.tdSm}>{r.material_desc || r.material_code}</td>
                <td style={tbl.tdSm}>{r.customer_name || (r.work_order ? `WO ${r.work_order}` : '—')}</td>
                <td style={{ ...tbl.tdRSm, fontWeight: 600 }}>{tons2(r.tons)}</td>
                <td style={tbl.tdSm}>
                  {n(r.tons) < 0 && <Pill tone="danger">negative</Pill>}
                  {n(r.tons) >= 15 && <Pill tone="danger">over 15 t</Pill>}
                  {n(r.tons) === Number(minTons) && <Pill tone="warning">at limit</Pill>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <div style={s.empty}>{q.isLoading ? 'Loading…' : 'No loads over the limit for these filters.'}</div>}
      </div>
      {list.length > 500 && <div style={s.note}>Showing the first 500 loads. Export Excel has all {fmtNumber(list.length)}.</div>}
    </div>
  );
}

function TicketsPosting({ range, yard, line, site }) {
  const [view, setView] = useState('wo');
  return (
    <div style={s.panel}>
      <div style={s.toolbar}>
        <Seg label="Show" value={view} onChange={setView}
             options={[['wo', 'Roll-off WO tickets'], ['unposted', 'Unposted third-party'], ['scale', 'Scale-Pak with no WO line']]} />
      </div>
      {view === 'wo' && <WoTickets range={range} yard={yard} site={site} />}
      {view === 'unposted' && <Unposted range={range} yard={yard} line={line} site={site} />}
      {view === 'scale' && <ScaleOnly range={range} />}
    </div>
  );
}

const PROBLEM_TONE = { match: 'success', 'WO only': 'warning', 'tons differ': 'danger', 'site differs': 'warning', 'material differs': 'warning', 'WO differs': 'warning' };

function WoTickets({ range, yard, site }) {
  const [problem, setProblem] = useState('');
  const [wo, setWo] = useState('');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const oneWo = /^\d{1,12}$/.test(wo.trim()) ? wo.trim() : '';
  const tooLong = !oneWo && range.days > LIST_DAYS;
  const params = oneWo ? { work_order: oneWo } : { from: range.from, to: range.to, ...(problem ? { problem } : {}) };
  const q = useQuery({
    queryKey: ['disposal', 'wo-tickets', params],
    queryFn: () => api.get('/disposal/wo-tickets', { params }),
    enabled: !tooLong,
    staleTime: 60_000,
  });
  const lines = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (q.data?.rows || []).filter((r) => (oneWo || ((!yard || r.yard === yard) && (!site || r.site_code === site)))
      && (!term || [r.ticket, r.customer_name, r.work_order, r.site_name, r.material_desc, r.service_address]
        .some((v) => String(v ?? '').toLowerCase().includes(term))));
  }, [q.data, yard, site, search, oneWo]);
  const count = (f) => lines.filter(f).length;
  const tally = { matched: count((r) => r.is_matched), clean: count((r) => r.problem === 'match'), woOnly: count((r) => r.problem === 'WO only') };
  const mismatch = ['tons differ', 'site differs', 'material differs', 'WO differs'].map((p) => ({ key: p, label: p, tons: count((r) => r.problem === p), loads: 0 }));

  return (
    <>
      <div style={s.toolbar}>
        <SelectOne label="Problem" value={problem} onChange={setProblem} width={170}
                   options={[{ value: '', label: 'All lines' }, { value: 'MISMATCH', label: 'Disagree with Scale-Pak' }, { value: 'WO ONLY', label: 'No scale ticket' }, { value: 'MATCH', label: 'Match' }]} />
        <TextField label="One work order" value={wo} onChange={setWo} placeholder="All its lines, any date" width={190} />
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticket, customer, WO, site, material"
               style={s.search} aria-label="Search work order tickets" />
        <ExportButton busy={exporting} disabled={!lines.length}
                      onClick={async () => { setExporting(true); try { await download('/disposal/wo-tickets/export.xlsx', params, oneWo ? `disposal-wo-${oneWo}.xlsx` : `disposal-wo-tickets-${range.from}-to-${range.to}.xlsx`); } finally { setExporting(false); } }} />
      </div>
      {tooLong && <div style={s.empty}>The work order list covers up to {LIST_DAYS} days, or one work order on any date.</div>}
      {q.isError && <div style={s.errorIn}>{q.error.message}</div>}
      {!tooLong && (
        <>
          <div style={s.tiles}>
            <StatTile icon={<Receipt size={16} />} label="Roll-off WO ticket lines" value={q.isLoading ? '…' : fmtNumber(lines.length)} sub="tons or a ticket number" />
            <StatTile icon={<Link2 size={16} />} tone="success" label="Matched to Scale-Pak" value={fmtNumber(tally.matched)} sub={lines.length ? `${((tally.matched / lines.length) * 100).toFixed(1)}%` : '—'} />
            <StatTile icon={<Scale size={16} />} label="No problem" value={fmtNumber(tally.clean)} sub="tons, site, material, WO agree" />
            <StatTile icon={<Link2Off size={16} />} tone={tally.woOnly ? 'warning' : 'neutral'} label="No scale ticket" value={fmtNumber(tally.woOnly)} sub="WO line only" onClick={() => setProblem('WO ONLY')} />
          </div>
          <div style={{ ...card.sm, minWidth: 0 }}>
            <div style={s.cardHead}>Where matched tickets disagree<span style={s.headNote}>first difference per line</span></div>
            <div style={s.chartBody}><Ranked items={mismatch.filter((x) => x.tons)} unit="" color={c.danger} /></div>
          </div>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Date', 'Problem', 'WO', 'Ticket', 'Customer', 'Yard', 'WO site', 'Scale site', 'Material', 'Status'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}
                  {['WO t', 'Scale t'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {lines.slice(0, 500).map((r) => (
                  <tr key={`${r.company_code}-${r.customer_number}-${r.work_order}-${r.line}`} style={tbl.tr}>
                    <td style={tbl.tdSm}>{fmtDate(r.ticket_date)}</td>
                    <td style={tbl.tdSm}><Pill tone={PROBLEM_TONE[r.problem]}>{r.problem}</Pill></td>
                    <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>
                      <button type="button" style={s.linkBtn} onClick={() => setWo(String(r.work_order))} title="All lines of this work order">{r.work_order}</button>
                    </td>
                    <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.ticket || '—'}</td>
                    <td style={tbl.tdSm} title={r.service_address || ''}>{r.customer_name || r.customer_number}</td>
                    <td style={tbl.tdSm}>{r.yard}</td>
                    <td style={tbl.tdSm} title={r.site_name || ''}>{r.site_code || '—'}</td>
                    <td style={tbl.tdSm}>{r.scale_site_code ? `${r.scale_site_code} · ${r.scale_facility}` : '—'}</td>
                    <td style={tbl.tdSm}>{r.material_desc || r.material_code}{r.scale_material_desc && r.material_matches === false ? ` / ${r.scale_material_desc}` : ''}</td>
                    <td style={tbl.tdSm}>{r.wo_status}</td>
                    <td style={tbl.tdRSm}>{tons2(r.tons)}</td>
                    <td style={tbl.tdRSm}>{r.scale_tons === null || r.scale_tons === undefined ? '—' : tons2(r.scale_tons)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!lines.length && <div style={s.empty}>{q.isLoading ? 'Loading…' : 'No work order ticket lines for these filters.'}</div>}
          </div>
          {lines.length > 500 && <div style={s.note}>Showing the first 500 lines. Export Excel has all {fmtNumber(lines.length)}.</div>}
        </>
      )}
    </>
  );
}

function Unposted({ range, yard, line, site }) {
  const [exporting, setExporting] = useState(false);
  const tooLong = range.days > LIST_DAYS;
  const params = { from: range.from, to: range.to, unposted: 'Y', ...(yard ? { yard } : {}), ...(site ? { site } : {}) };
  const q = useQuery({
    queryKey: ['disposal', 'tickets', params],
    queryFn: () => api.get('/disposal/tickets', { params }),
    enabled: !tooLong,
    staleTime: 60_000,
  });
  const list = useMemo(() => (q.data?.rows || []).filter((r) => !line || r.line_of_business === line), [q.data, line]);
  const bands = ageBands(list, pacificToday());
  if (tooLong) return <div style={s.empty}>The unposted list covers up to {LIST_DAYS} days. Choose a shorter range.</div>;
  return (
    <>
      <div style={s.grid2}>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Unposted tickets by age<span style={s.headNote}>days since the ticket date</span></div>
          <div style={s.chartBody}><Ranked items={bands} unit="" keepOrder color={c.warning} /></div>
        </div>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>What is listed</div>
          <div style={{ ...s.chartBody, ...s.note }}>
            <p style={s.p}>Route-truck tickets from third-party sites that are keyed in but not posted yet ((Un)posted Disposal Data Inquiry). Athens facilities are left out, as Crystal did; the list of them is in the reporting model and Accounting confirms it.</p>
          </div>
        </div>
      </div>
      <div style={s.toolbar}>
        <span style={s.note}>{q.isLoading ? 'Loading…' : `${fmtNumber(list.length)} unposted tickets, ${fmtNumber(Math.round(list.reduce((a, r) => a + n(r.tons), 0)))} t`}</span>
        <ExportButton busy={exporting} disabled={!list.length}
                      onClick={async () => { setExporting(true); try { await download('/disposal/tickets/export.xlsx', params, `disposal-unposted-${range.from}-to-${range.to}.xlsx`); } finally { setExporting(false); } }} />
      </div>
      {q.isError && <div style={s.errorIn}>{q.error.message}</div>}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              {['Date', 'Yard', 'Route', 'Truck', 'Driver', 'Load', 'Ticket', 'Site', 'Material'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}
              {['Tons', 'Cost', 'Days'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {list.slice(0, 500).map((r) => {
              const age = Math.round((Date.parse(pacificToday()) - Date.parse(day(r.disposal_date))) / 86400000);
              return (
                <tr key={`${r.company_code}-${r.date_number}-${r.route_code}-${r.load_number}-${r.ticket}`} style={tbl.tr}>
                  <td style={tbl.tdSm}>{fmtDate(r.disposal_date)}</td>
                  <td style={tbl.tdSm}>{r.yard}</td>
                  <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.route_code}</td>
                  <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.vehicle || '—'}</td>
                  <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.driver || '—'}</td>
                  <td style={tbl.tdSm}>{r.load_number}</td>
                  <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.ticket || '—'}</td>
                  <td style={tbl.tdSm} title={r.site_code}>{r.site_name || r.site_code}</td>
                  <td style={tbl.tdSm}>{r.material_desc || r.material_code}</td>
                  <td style={tbl.tdRSm}>{tons2(r.tons)}</td>
                  <td style={tbl.tdRSm}>{fmtMoney(r.cost)}</td>
                  <td style={tbl.tdRSm}>{age > 7 ? <Pill tone={age > 30 ? 'danger' : 'warning'}>{age}</Pill> : age}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!list.length && <div style={s.empty}>{q.isLoading ? 'Loading…' : 'Nothing unposted for these filters.'}</div>}
      </div>
    </>
  );
}

function ScaleOnly({ range }) {
  const tooLong = range.days > LIST_DAYS;
  const params = { from: range.from, to: range.to };
  const q = useQuery({
    queryKey: ['disposal', 'scale-only', params],
    queryFn: () => api.get('/disposal/scale-only', { params }),
    enabled: !tooLong,
    staleTime: 60_000,
  });
  const list = q.data?.rows || [];
  if (tooLong) return <div style={s.empty}>This list covers up to {LIST_DAYS} days. Choose a shorter range.</div>;
  return (
    <>
      <div style={s.note}>Scale-Pak weight tickets that name a work order whose lines carry no matching ticket: the load was weighed, the work order does not show it.</div>
      {q.isError && <div style={s.errorIn}>{q.error.message}</div>}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              {['Date', 'Facility', 'Ticket', 'Work order', 'Route', 'Truck', 'Site', 'Material'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}
              <th style={tbl.thRSm}>Tons</th>
            </tr>
          </thead>
          <tbody>
            {list.slice(0, 500).map((r) => (
              <tr key={`${r.facility_code}-${r.ticket}-${r.line}`} style={tbl.tr}>
                <td style={tbl.tdSm}>{fmtDate(r.ticket_date)}</td>
                <td style={tbl.tdSm}>{r.facility_code}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.ticket}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.work_order}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.route_code || '—'}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.vehicle || '—'}</td>
                <td style={tbl.tdSm}>{r.site_code || '—'}</td>
                <td style={tbl.tdSm}>{r.material_code}</td>
                <td style={tbl.tdRSm}>{tons2(r.tons)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <div style={s.empty}>{q.isLoading ? 'Loading…' : 'Every weighed work order load has its ticket line.'}</div>}
      </div>
    </>
  );
}

function Cost({ range, yard, site }) {
  const [measure, setMeasure] = useState('cost');
  const [exporting, setExporting] = useState(false);
  const q = useQuery({
    queryKey: ['disposal', 'charge-daily', { from: range.from, to: range.to }],
    queryFn: () => api.get('/disposal/charge-daily', { params: { from: range.from, to: range.to } }),
    staleTime: 60_000,
  });
  const all = useMemo(() => (q.data?.rows || []).filter((r) => (!yard || r.yard === yard) && (!site || r.site_code === site)), [q.data, yard, site]);
  const variable = all.filter((r) => r.in_variable_disposal);
  const sub = all.filter((r) => r.is_subhaul);
  const sum = (rows, k) => rows.reduce((a, r) => a + n(r[k]), 0);
  const vTons = sum(variable, 'tons');
  const vCost = sum(variable, 'cost');
  const over = sum(variable, 'rated_cost') - sum(variable, 'rated_contract_cost');
  const noGl = sum(variable.filter((r) => r.gl_account === 'ERROR'), 'charges');
  const matrix = useMemo(() => glMatrix(variable, measure), [variable, measure]); // eslint-disable-line react-hooks/exhaustive-deps
  const rates = useMemo(() => {
    const m = new Map();
    for (const r of variable) {
      if (!n(r.rated_tons)) continue;
      const k = `${r.site_code}|${r.material_code}`;
      const x = m.get(k) || { key: k, site: r.site_name || r.site_code, material: r.material_desc || r.material_code, tons: 0, cost: 0, contract: 0 };
      x.tons += n(r.rated_tons); x.cost += n(r.rated_cost); x.contract += n(r.rated_contract_cost);
      m.set(k, x);
    }
    return [...m.values()].map((x) => ({ ...x, over: x.cost - x.contract })).sort((a, b) => b.over - a.over);
  }, [variable]);
  const accounts = useMemo(() => {
    const m = new Map();
    for (const r of sub) {
      const k = `${r.subhaul_company}-${r.subhaul_customer_number}`;
      const x = m.get(k) || { key: k, name: r.subhaul_customer_name || k, charges: 0, tons: 0, cost: 0 };
      x.charges += n(r.charges); x.tons += n(r.tons); x.cost += n(r.cost);
      m.set(k, x);
    }
    return [...m.values()].sort((a, b) => b.cost - a.cost);
  }, [sub]);
  const fmtCell = (v) => (v === null || v === undefined ? '—' : measure === 'tons' ? fmtNumber(Math.round(v)) : measure === 'rate' ? money2(v) : fmtMoney(v));
  const tooLong = range.days > LIST_DAYS;
  const exportParams = { from: range.from, to: range.to, ...(site ? { site } : {}) };

  return (
    <div style={s.panel}>
      {q.isError && <div style={s.errorIn}>{q.error.message}</div>}
      <div style={s.tiles}>
        <StatTile icon={<DollarSign size={16} />} label="Third-party disposal cost" value={fmtMoney(vCost)} sub="landfill charges (Variable Disposal)" />
        <StatTile icon={<Weight size={16} />} label="Tons" value={fmtNumber(Math.round(vTons))} sub={q.isLoading ? 'Loading…' : `${fmtNumber(sum(variable, 'charges'))} charges`} />
        <StatTile icon={<Scale size={16} />} label="Paid $/ton" value={vTons ? money2(vCost / vTons) : '—'} sub="cost ÷ tons" />
        <StatTile icon={<AlertTriangle size={16} />} tone={over > 0.5 ? 'danger' : 'neutral'} label="Over contract rate" value={fmtMoney(Math.max(0, over))} sub="where the site has a rate" />
        <StatTile icon={<ClipboardX size={16} />} tone={noGl ? 'warning' : 'neutral'} label="No G/L account" value={fmtNumber(noGl)} sub="material not mapped" />
      </div>

      <div style={{ ...card.sm, minWidth: 0 }}>
        <div style={s.cardHead}>
          Cost by division and G/L account
          <Seg value={measure} onChange={setMeasure} options={[['cost', 'Cost'], ['tons', 'Tons'], ['rate', '$/ton']]} />
        </div>
        <div style={s.tableWrapFlat}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={tbl.thSm}>Division</th>
                {matrix.accounts.map((a) => <th key={a} style={tbl.thRSm}>{a}</th>)}
                <th style={tbl.thRSm}>Total</th>
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((x) => (
                <tr key={x.division} style={tbl.tr}>
                  <td style={{ ...tbl.tdSm, fontWeight: 600 }}>{x.division} {x.yard && <span style={s.sub}>{x.yard}</span>}</td>
                  {x.values.map((v, i) => <td key={matrix.accounts[i]} style={tbl.tdRSm}>{fmtCell(v)}</td>)}
                  <td style={{ ...tbl.tdRSm, fontWeight: 600 }}>{fmtCell(x.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!matrix.rows.length && <div style={s.empty}>{q.isLoading ? 'Loading…' : 'No landfill charges for these filters.'}</div>}
        </div>
      </div>

      <div style={s.grid2}>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Paid against contract rate<span style={s.headNote}>LFRC cost ÷ tons against the site rate</span></div>
          <div style={s.tableWrapFlat}>
            <table style={s.table}>
              <thead><tr>{['Site', 'Material'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}{['Tons', 'Paid $/t', 'Contract $/t', 'Difference'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}</tr></thead>
              <tbody>
                {rates.slice(0, 25).map((x) => (
                  <tr key={x.key} style={tbl.tr}>
                    <td style={tbl.tdSm}>{x.site}</td>
                    <td style={tbl.tdSm}>{x.material}</td>
                    <td style={tbl.tdRSm}>{fmtNumber(Math.round(x.tons))}</td>
                    <td style={tbl.tdRSm}>{money2(x.cost / x.tons)}</td>
                    <td style={tbl.tdRSm}>{money2(x.contract / x.tons)}</td>
                    <td style={tbl.tdRSm}>{Math.abs(x.over) < 0.5 ? <Pill tone="success">on rate</Pill> : <Pill tone={x.over > 0 ? 'danger' : 'success'}>{x.over > 0 ? '' : '−'}{fmtMoney(Math.abs(x.over))}</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rates.length && <div style={s.empty}>No charge here has a contract rate on file.</div>}
          </div>
        </div>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>City sub-haul accounts<span style={s.headNote}>cycle IN</span></div>
          <div style={s.tableWrapFlat}>
            <table style={s.table}>
              <thead><tr><th style={tbl.thSm}>Account</th>{['Loads', 'Tons', 'Cost'].map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}</tr></thead>
              <tbody>
                {accounts.map((x) => (
                  <tr key={x.key} style={tbl.tr}>
                    <td style={tbl.tdSm}>{x.name} <span style={s.code}>{x.key}</span></td>
                    <td style={tbl.tdRSm}>{fmtNumber(x.charges)}</td>
                    <td style={tbl.tdRSm}>{n(x.tons).toFixed(1)}</td>
                    <td style={tbl.tdRSm}>{fmtMoney(x.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!accounts.length && <div style={s.empty}>No sub-haul loads in the range.</div>}
          </div>
        </div>
      </div>

      <div style={s.toolbar}>
        <span style={s.note}>Rates typed into the Crystal reports (CLARTS at $87.00, the sub-haul rates) now come from one dated rate table; a charge priced from it says so in the export.</span>
        <button type="button" style={btn.secondarySm} disabled={tooLong || exporting}
                title={tooLong ? `Up to ${LIST_DAYS} days` : ''}
                onClick={async () => { setExporting(true); try { await download('/disposal/charges/export.xlsx', exportParams, `disposal-charges-${range.from}-to-${range.to}.xlsx`); } finally { setExporting(false); } }}>
          <Download size={13} />{exporting ? 'Exporting…' : 'Export charges'}
        </button>
      </div>
    </div>
  );
}

const IDLE_BANDS = [[31, 45, '31–45 days'], [46, 60, '46–60 days'], [61, 90, '61–90 days'], [91, 180, '91–180 days'], [181, Infinity, 'Over 180 days']];

function Idle({ yard }) {
  const [days, setDays] = useState('30');
  const [owned, setOwned] = useState('');
  const [area, setArea] = useState('');
  const [vip, setVip] = useState('INCLUDE');
  const [rep, setRep] = useState('');
  const [exporting, setExporting] = useState(false);
  const overDays = /^\d{1,4}$/.test(days) ? days : '30';
  const params = { over_days: overDays, vip, ...(yard ? { yard } : {}), ...(owned ? { owned_by: owned } : {}), ...(area ? { area } : {}), ...(rep.trim() ? { sales_rep: rep.trim().toUpperCase() } : {}) };
  const q = useQuery({
    queryKey: ['disposal', 'idle-containers', params],
    queryFn: () => api.get('/disposal/idle-containers', { params }),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const list = q.data?.rows || [];
  const bands = IDLE_BANDS.map(([lo, hi, label]) => ({ key: label, label, tons: list.filter((r) => n(r.days_idle) >= lo && n(r.days_idle) <= hi).length, loads: 0 }))
    .filter((b) => b.tons);
  const sizes = useMemo(() => {
    const cols = ['10–20 yd', '25–35 yd', '40 yd +', 'Compactor'];
    const col = (r) => (r.container_type === 'Compactor' ? 3 : n(r.container_size) <= 20 ? 0 : n(r.container_size) <= 35 ? 1 : 2);
    const m = new Map();
    for (const r of list) {
      const x = m.get(r.yard) || { yard: r.yard, counts: [0, 0, 0, 0] };
      x.counts[col(r)] += 1;
      m.set(r.yard, x);
    }
    return { cols, rows: [...m.values()].sort((a, b) => b.counts.reduce((s, v) => s + v, 0) - a.counts.reduce((s, v) => s + v, 0)) };
  }, [list]);

  return (
    <div style={s.panel}>
      <div style={s.toolbar}>
        <TextField label="Idle more than (days)" value={days} onChange={(v) => setDays(v.replace(/[^0-9]/g, '').slice(0, 4))} width={150} />
        <SelectOne label="Owned by" value={owned} onChange={setOwned} width={130} options={[{ value: '', label: 'All' }, { value: 'COMPANY', label: 'Company' }, { value: 'CUSTOMER', label: 'Customer' }]} />
        <SelectOne label="Area" value={area} onChange={setArea} width={140} options={[{ value: '', label: 'Open + Franchise' }, { value: 'OPEN', label: 'Open market' }, { value: 'FRANCHISE', label: 'Franchise' }]} />
        <SelectOne label="VIP" value={vip} onChange={setVip} width={130} options={[{ value: 'INCLUDE', label: 'Include' }, { value: 'EXCLUDE', label: 'Leave out' }, { value: 'ONLY', label: 'VIP only' }]} />
        <TextField label="Sales rep" value={rep} onChange={setRep} placeholder="ID" width={110} />
        <button type="button" style={btn.primarySm} disabled={exporting || !list.length}
                onClick={async () => { setExporting(true); try { await download('/disposal/idle-containers/export.xlsx', params, `idle-containers-over-${overDays}-days.xlsx`); } finally { setExporting(false); } }}>
          <Download size={13} />{exporting ? 'Exporting…' : 'Billing list'}
        </button>
      </div>
      {q.isError && (
        <div style={s.errorIn}>
          {q.error.message} Idle containers is switched on once its bin-type list is confirmed on the replica (docs/disposal.md).
        </div>
      )}
      <div style={s.tiles}>
        <StatTile icon={<Box size={16} />} tone={list.length ? 'warning' : 'neutral'} label={`Idle more than ${overDays} days`} value={q.isLoading ? '…' : fmtNumber(list.length)} sub="permanent roll-off boxes" />
        <StatTile icon={<AlertTriangle size={16} />} tone="danger" label="Idle more than 90 days" value={fmtNumber(list.filter((r) => n(r.days_idle) > 90).length)} sub={list.length ? `${((list.filter((r) => n(r.days_idle) > 90).length / list.length) * 100).toFixed(0)}%` : '—'} />
        <StatTile icon={<Truck size={16} />} label="Company owned" value={fmtNumber(list.filter((r) => r.owned_by === 'Company').length)} sub={`${fmtNumber(list.filter((r) => r.owned_by === 'Customer').length)} customer owned`} />
        <StatTile icon={<Receipt size={16} />} label="VIP accounts" value={fmtNumber(list.filter((r) => r.is_vip).length)} sub="review before billing" />
      </div>
      <div style={s.grid2}>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>Days idle</div>
          <div style={s.chartBody}><Ranked items={bands} unit="" keepOrder /></div>
        </div>
        <div style={{ ...card.sm, minWidth: 0 }}>
          <div style={s.cardHead}>By yard and size</div>
          <div style={s.tableWrapFlat}>
            <table style={s.table}>
              <thead><tr><th style={tbl.thSm}>Yard</th>{sizes.cols.map((h) => <th key={h} style={tbl.thRSm}>{h}</th>)}</tr></thead>
              <tbody>
                {sizes.rows.map((x) => (
                  <tr key={x.yard} style={tbl.tr}>
                    <td style={{ ...tbl.tdSm, fontWeight: 600 }}>{x.yard}</td>
                    {x.counts.map((v, i) => <td key={sizes.cols[i]} style={tbl.tdRSm}>{v || '—'}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              {['Account', 'Customer', 'Service address', 'Box', 'Size · type', 'Owner', 'Last WO'].map((h) => <th key={h} style={tbl.thSm}>{h}</th>)}
              <th style={tbl.thRSm}>Days idle</th>
              {['Rep', ''].map((h) => <th key={h || 'flags'} style={tbl.thSm}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {list.slice(0, 500).map((r) => (
              <tr key={`${r.account}-${r.container_number}`} style={tbl.tr}>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.account}</td>
                <td style={tbl.tdSm}>{r.customer_name}</td>
                <td style={tbl.tdSm}>{[r.service_street, r.service_city].filter(Boolean).join(', ')}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.container_number}</td>
                <td style={tbl.tdSm}>{r.container_size} · {r.container_type}</td>
                <td style={tbl.tdSm}>{r.owned_by || '—'}</td>
                <td style={{ ...tbl.tdSm, fontFamily: t.familyMono }}>{r.last_work_order} · {fmtDate(r.last_date)}</td>
                <td style={{ ...tbl.tdRSm, fontWeight: 600 }}>{r.days_idle}</td>
                <td style={tbl.tdSm}>{r.sales_rep || '—'}</td>
                <td style={tbl.tdSm}>
                  {r.is_vip && <Pill tone="info">VIP</Pill>}
                  {r.waste_type === 'FOODWASTE' && <Pill tone="warning">food waste</Pill>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && !q.isError && <div style={s.empty}>{q.isLoading ? 'Loading…' : 'No containers idle that long.'}</div>}
      </div>
      {list.length > 500 && <div style={s.note}>Showing the first 500 containers. The billing list has all {fmtNumber(list.length)}.</div>}
    </div>
  );
}

function Legend({ items }) {
  return (
    <span style={s.legend}>
      {items.map(([label, color]) => (
        <span key={label} style={s.legendItem}><i style={{ ...s.swatch, background: color }} />{label}</span>
      ))}
    </span>
  );
}

// Magnitude for one nominal dimension: one hue; a picked bar stays dark, the rest recede.
// `tons` is the bar's value whatever it counts (tons, loads, tickets).
function Ranked({ items, selected, onPick, unit = '', color = chart.emphasis, keepOrder = false }) {
  if (!items.length) return <div style={s.empty}>Nothing for these filters.</div>;
  const list = keepOrder ? items : [...items].sort((a, b) => b.tons - a.tons);
  const max = Math.max(...list.map((x) => x.tons), 1);
  const Row = onPick ? 'button' : 'div';
  return (
    <div style={s.ranked}>
      {list.slice(0, 14).map((x) => (
        <Row key={x.key} {...(onPick ? { type: 'button', onClick: () => onPick(x.key), 'aria-pressed': selected === x.key } : {})}
             style={{ ...s.rankRow, cursor: onPick ? 'pointer' : 'default' }} title={`${x.label}: ${fmtNumber(Math.round(x.tons))}${unit}`}>
          <span style={s.rankLabel}>{x.label}</span>
          <span style={s.rankTrack}>
            <span style={{ ...s.rankBar, width: `${(x.tons / max) * 100}%`, background: selected && selected !== x.key ? chart.deselectedLight : color }} />
          </span>
          <span style={s.rankValue}>{fmtNumber(Math.round(x.tons))}{unit}</span>
        </Row>
      ))}
      {list.length > 14 && <div style={s.note}>{list.length - 14} more in the pick list above.</div>}
    </div>
  );
}

function Seg({ label, value, onChange, options }) {
  const group = (
    <div style={s.seg} role="group" aria-label={label || 'Choose'}>
      {options.map(([v, l]) => (
        <button key={v} type="button" aria-pressed={value === v}
                style={{ ...s.segBtn, ...(value === v ? s.segOn : null) }} onClick={() => onChange(v)}>
          {l}
        </button>
      ))}
    </div>
  );
  if (!label) return group;
  return <div style={s.field}><span style={s.label}>{label}</span>{group}</div>;
}

const PILL = {
  neutral: { color: c.textStrong, background: c.canvas },
  info:    { color: c.info, background: c.infoTint },
  success: { color: c.successText, background: c.successTint },
  warning: { color: c.warningText, background: c.warningTint },
  danger:  { color: c.dangerText, background: c.dangerTint },
};
function Pill({ tone = 'neutral', children }) {
  return <span style={{ ...s.pill, ...(PILL[tone] || PILL.neutral) }}>{children}</span>;
}

function ExportButton({ busy, disabled, onClick }) {
  return (
    <button type="button" style={btn.secondarySm} onClick={onClick} disabled={busy || disabled}>
      <Download size={13} />{busy ? 'Exporting…' : 'Export Excel'}
    </button>
  );
}

const s = {
  live:      { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: c.successText, background: c.successTint, borderRadius: '999px', padding: '2px 9px' },
  dot:       { width: 6, height: 6, borderRadius: '50%', background: c.success },
  filters:   { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', padding: '14px 18px' },
  field:     { display: 'flex', flexDirection: 'column', gap: '4px' },
  label:     { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: c.textMuted },
  seg:       { display: 'inline-flex', height: '32px', border: `1px solid ${c.border}`, borderRadius: '6px', overflow: 'hidden', background: c.surface },
  segBtn:    { border: 0, borderRight: `1px solid ${c.border}`, background: 'transparent', color: c.textMuted, padding: '0 11px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  segOn:     { background: c.navy, color: '#fff', fontWeight: 600 },
  tabRow:    { display: 'flex', borderBottom: `1px solid ${c.border}`, padding: '0 18px', overflowX: 'auto' },
  // Longhand bottom border, as on Scale-Pak: mixing a shorthand with a longhand on rerender leaves stale styles.
  tab:       { background: 'none', borderTop: 0, borderLeft: 0, borderRight: 0, padding: '10px 2px', marginRight: '22px', fontSize: '13px', fontWeight: 600, color: c.textFaint, borderBottomWidth: '2px', borderBottomStyle: 'solid', borderBottomColor: 'transparent', marginBottom: '-1px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tabOn:     { color: c.navy, borderBottomColor: c.navy },
  panel:     { padding: '18px', display: 'grid', gap: '16px' },
  tiles:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' },
  grid2:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '16px' },
  grid2b:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '16px' },
  cardHead:  { ...card.head, borderRadius: '10px 10px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  headNote:  { fontSize: '11px', fontWeight: 400, color: c.textFaint },
  chartBody: { padding: '14px 18px 12px' },
  legend:    { display: 'inline-flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', fontSize: '11px', fontWeight: 400, color: c.textMuted },
  legendItem:{ display: 'inline-flex', alignItems: 'center', gap: '5px' },
  swatch:    { display: 'inline-block', width: 10, height: 10, borderRadius: 2 },
  ranked:    { display: 'grid', gap: '4px' },
  rankRow:   { display: 'grid', gridTemplateColumns: 'minmax(90px, 170px) minmax(0, 1fr) 64px', gap: '8px', alignItems: 'center', background: 'none', border: 0, padding: '3px 2px', textAlign: 'left', borderRadius: '4px', fontFamily: 'inherit' },
  rankLabel: { fontSize: '11.5px', color: c.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rankTrack: { height: '14px' },
  rankBar:   { display: 'block', height: '100%', borderRadius: '0 4px 4px 0' },
  rankValue: { fontSize: '10.5px', color: c.textMuted, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  toolbar:   { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
  search:    { flex: '1 1 240px', minWidth: 0, height: '32px', padding: '0 10px', border: `1px solid ${c.border}`, borderRadius: '6px', fontSize: '12px', fontFamily: 'inherit' },
  // Long lists scroll inside their own box, so a 500-row list does not push the page to 7,000 px.
  tableWrap: { overflow: 'auto', maxHeight: '560px', border: `1px solid ${c.border}`, borderRadius: '10px' },
  tableWrapFlat: { overflowX: 'auto' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  rowOn:     { background: c.infoTint },
  sub:       { fontSize: '11px', fontWeight: 400, color: c.textFaint, marginLeft: '4px' },
  code:      { fontFamily: t.familyMono, fontSize: '10.5px', color: c.textFaint, marginLeft: '4px' },
  pill:      { display: 'inline-block', fontSize: '10.5px', fontWeight: 600, borderRadius: '4px', padding: '1px 6px', marginRight: '4px', whiteSpace: 'nowrap' },
  linkBtn:   { ...btn.linkXs, fontFamily: t.familyMono },
  note:      { fontSize: '12px', color: c.textMuted },
  p:         { margin: 0, lineHeight: 1.5 },
  empty:     { fontSize: '12px', color: c.textFaint, padding: '24px 0', textAlign: 'center' },
  error:     { margin: '12px 18px 0', padding: '10px 12px', borderRadius: '8px', background: c.dangerTint, color: c.dangerText, fontSize: '12px' },
  errorIn:   { padding: '10px 12px', borderRadius: '8px', background: c.dangerTint, color: c.dangerText, fontSize: '12px' },
};
